import { BASE_URL } from "@config/apiConfig";
import keycloak from "@config/keycloak";
import { startLoginRedirect } from "@telemetry/trackLogin";
import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

// Use your root gateway; individual services are appended later.
const API_BASE = BASE_URL;

// single-flight refresh guard
let refreshing: Promise<boolean> | null = null;

function currentPath() {
  return typeof window === "undefined" ? "" : window.location.pathname;
}

export async function ensureFreshToken(): Promise<string | undefined> {
  if (!keycloak.authenticated) return undefined;

  try {
    if (!refreshing) {
      refreshing = keycloak.updateToken(30).finally(() => {
        // allow another refresh attempt on next request
        setTimeout(() => (refreshing = null), 0);
      });
    }
    await refreshing;
  } catch {
    // refresh failed -> go to login
    startLoginRedirect(
      {
        path: currentPath(),
        trigger: "http_refresh_failed",
      },
      () => keycloak.login(),
    );
    return undefined;
  }
  return keycloak.token ?? undefined;
}

export function createHttp(baseURL = API_BASE): AxiosInstance {
  const http = axios.create({
    baseURL,
    withCredentials: false, // flip to true if your API needs cookies
  });

  // Attach Bearer on every request
  http.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await ensureFreshToken();
    if (token) {
      const headers = new AxiosHeaders(config.headers);
      headers.set("Authorization", `Bearer ${token}`);
      config.headers = headers;
    }
    return config;
  });

  // Retry once on 401 after a forced refresh; otherwise trigger login
  http.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as
        | (InternalAxiosRequestConfig & {
            _retry?: boolean;
          })
        | undefined;

      if (error.response?.status === 401 && original && !original._retry) {
        original._retry = true;
        try {
          await keycloak.updateToken(0);
          const t = keycloak.token;
          if (t) {
            const headers = new AxiosHeaders(original.headers);
            headers.set("Authorization", `Bearer ${t}`);
            original.headers = headers;
            return axios(original);
          }
        } catch {
          // fall through
        }
        startLoginRedirect(
          {
            path: currentPath(),
            trigger: "http_401_refresh_failed",
          },
          () => keycloak.login(),
        );
      }
      return Promise.reject(error);
    },
  );

  return http;
}

// Export a shared instance for most services
export const http = createHttp();
